import { useState, useEffect } from 'react';
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useAccountFormStore } from "../../../store/accountFormStore";
import { useAuthStore } from "../../../store/authStore";
import { ACTIONS, TOP_ACCOUNT_BUILDER_INSTRUCTIONS } from "../../../utils/constants";
import { useCSVReader } from 'react-papaparse';
import { parseAccountsCsvData, CSV_HEADER, REQUIRED_ACCOUNT_CSV_HEADERS } from './AccountBuilderCsv';
import { submitAccountBatchJob, fetchGetApiRequestJobStatus, submitV2Audit, getAccountTypes, getBillingCycles } from '../../../utils/apiUtils';
import { delay } from '../../../utils/commonUtils';

const spinnerStyles = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

const DownloadCSVTemplateButton = () => {
    const handleDownload = () => {
        const blob = new Blob([CSV_HEADER], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'account_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    return (
        <button
            type="button"
            style={{ width: '180px', height: '45px' }}
            className="btn"
            onClick={handleDownload}
        >
            Download CSV Template
        </button>
    );
};

const AccountBuilder = () => {
    const navigate = useNavigate();
    const { accounts, addAccount, removeAccountByAccountId } = useAccountFormStore();
    const email = useAuthStore((state) => state.email) || 'unknown@centivo.com';

    const [csvData, setCsvData] = useState(null);
    const [csvError, setCsvError] = useState(null);
    const [csvParseError, setCsvParseError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionProgress, setSubmissionProgress] = useState('');
    const [submitResults, setSubmitResults] = useState(null);

    useEffect(() => {
        const styleEl = document.createElement('style');
        styleEl.textContent = spinnerStyles;
        document.head.appendChild(styleEl);
        return () => document.head.removeChild(styleEl);
    }, []);

    const { data: accountTypes, isLoading: atLoading } = useQuery({
        queryKey: ['getAccountTypes'],
        queryFn: () => getAccountTypes(),
        select: (data) => data?.rows.filter(row => row.accountTypeCode.includes('AT')).map((entry) => {
            return { value: entry.accountTypeCode, label: entry.accountTypeDesc }
        })
    });

    const { data: billingCycles, isLoading: bcLoading } = useQuery({
        queryKey: ['getBillingCycles'],
        queryFn: () => getBillingCycles(),
        select: (data) => data?.rows.map((entry) => {
            return { value: entry.billingCycleFactKey, label: entry.billingCycleName }
        })
    });

    const isLoading = atLoading || bcLoading;

    const getDateString = (date) => {
        if (date) {
            if (typeof date === 'string') {
                return date.substring(0, 10);
            } else if (date instanceof Date) {
                return date.toISOString().substring(0, 10);
            }
        }
        return '';
    };

    const { CSVReader } = useCSVReader();
    const CSVButton = ({ children }) => {
        return (
            <CSVReader
                config={{ header: true, skipEmptyLines: true, }}
                onUploadAccepted={onUploadAccepted} >
                {({
                    getRootProps,
                    ProgressBar,
                }) => (
                    <div style={{ display: "grid" }} >
                        <button type="button" style={{ width: '180px', height: '45px' }} className="btn edit-btn" {...getRootProps()}>
                            {children}
                        </button>
                        <ProgressBar style={{ height: '15px' }} />
                    </div>
                )}
            </CSVReader>
        );
    };

    const onUploadAccepted = async (results) => {
        try {
            setCsvError(null);
            setCsvParseError(null);
            const parsed = parseAccountsCsvData(results.data, accountTypes, billingCycles);
            setCsvData(parsed);
        } catch (err) {
            setCsvParseError(err.message);
        }
    };

    const onSubmit = async () => {
        setIsSubmitting(true);
        setCsvError(null);
        setSubmissionProgress('Submitting batch job...');
        try {
            const jobKeys = await submitAccountBatchJob({ accounts: csvData });
            if (!jobKeys?.pk || !jobKeys?.sk) {
                throw new Error("Invalid job response from server");
            }
            setSubmissionProgress('Processing accounts...');
            let jobStatusRes;
            let pollCount = 0;
            do {
                await delay(3000);
                pollCount++;
                setSubmissionProgress(`Processing accounts... (${pollCount * 3}s elapsed)`);
                jobStatusRes = await fetchGetApiRequestJobStatus({
                    pk: jobKeys.pk,
                    sk: jobKeys.sk,
                });
            } while (jobStatusRes.requestStatus === "PENDING");

            setSubmissionProgress('Finalizing submission...');
            let result = jobStatusRes.responseBody;
            await submitV2Audit({
                entityType: "ACCOUNT",
                entityId: csvData[0].topAccountHccId,
                action: "Bulk Update",
                email,
                request: result.bodies,
                response: result.responses,
            });
            setIsSubmitting(false);
            setSubmissionProgress('');
            setSubmitResults(result);

            const successCount = result.bodies.length - (result.failures?.length || 0);
            const failureCount = result.failures?.length || 0;

            if (failureCount === 0) {
                toast.success(`Successfully submitted ${successCount} account${successCount !== 1 ? 's' : ''}.`);
            } else if (successCount === 0) {
                toast.error(`Failed to submit all ${failureCount} account${failureCount !== 1 ? 's' : ''}. See details below.`);
            } else {
                toast.warning(`Submitted ${successCount} account${successCount !== 1 ? 's' : ''} successfully, but ${failureCount} failed. See details below.`);
            }
        } catch (err) {
            setCsvError(err.message);
            setIsSubmitting(false);
            setSubmissionProgress('');
            toast.error(err.message || "Failed to submit accounts.");
        }
    };

    const onClear = () => {
        setCsvData(null);
        setCsvError(null);
        setCsvParseError(null);
        setSubmissionProgress('');
        setSubmitResults(null);
    };

    if (submitResults) {
        const errors = new Array(submitResults.bodies.length);
        submitResults.failures.forEach(f => {
            errors[f.index] = f.error;
        })
        return (
            <div className="container" style={{ position: "relative" }}>
                <p className="title">Submission Results</p>
                <div style={{ overflowY: 'auto', overflowX: 'auto', marginBottom: '1rem' }}>
                    <table>
                        <thead>
                            <tr>
                                <th>HCC ID</th>
                                <th>Status</th>
                                <th>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {submitResults.bodies.map((body, i) => {
                                const success = !errors[i];
                                const style = { backgroundColor: (success) ? 'lightgreen' : 'lightred' };
                                return (
                                    <tr key={i}>
                                        <th style={style}>
                                            {body.account.accountHccIdentifier}
                                        </th>
                                        <th style={style}>
                                            {(success) ? "Success" : "Failure"}
                                        </th>
                                        <th style={style}>
                                            {errors[i]}
                                        </th>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem", }}>
                    <button style={{ width: '180px', height: '45px' }} type="button" className="btn" onClick={onClear}>
                        Finished
                    </button>
                </div>
            </div>
        );
    }

    if (csvData) {
        return (
            <div className="container" style={{ position: "relative" }}>
                <p className="title">CSV Preview</p>
                {csvError && (
                    <div style={{
                        backgroundColor: '#fee',
                        border: '2px solid #d33',
                        borderRadius: '4px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        color: '#800'
                    }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#d33' }}>Submission Error</h4>
                        <p style={{ margin: 0 }}>{csvError}</p>
                    </div>
                )}
                {isSubmitting && submissionProgress && (
                    <div style={{
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196f3',
                        borderRadius: '4px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                    }}>
                        <div style={{
                            border: '3px solid #2196f3',
                            borderTop: '3px solid transparent',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            animation: 'spin 1s linear infinite'
                        }} />
                        <span style={{ fontWeight: 500, color: '#1565c0' }}>{submissionProgress}</span>
                    </div>
                )}
                <div style={{ overflowY: 'auto', overflowX: 'auto', marginBottom: '1rem' }}>
                    <table>
                        <thead>
                            <tr>
                                {REQUIRED_ACCOUNT_CSV_HEADERS.map((h, i) => <th key={i}>{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {csvData.map((account, i) => {
                                return (
                                    <tr key={i}>
                                        {REQUIRED_ACCOUNT_CSV_HEADERS.map((h, j) => <td key={`${i}.${j}`}>{account[h]}</td>)}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem", }}>
                    <button style={{ width: '180px', height: '45px' }} type="button" className="btn edit-btn" disabled={!csvError && isSubmitting} onClick={onSubmit}>
                        {isSubmitting ? 'Submitting...' : 'Submit Account'}
                    </button>
                    <CSVButton>Re-upload CSV</CSVButton>
                    <button style={{ width: '180px', height: '45px' }} type="button" className="btn" onClick={onClear}>
                        Clear
                    </button>
                </div>
            </div>
        );
    } else if (csvParseError) {
        return (
            <div className="container" style={{ position: "relative" }}>
                <p className="title">CSV Upload Error</p>
                <div style={{
                    backgroundColor: '#fee',
                    border: '2px solid #d33',
                    borderRadius: '4px',
                    padding: '1.5rem',
                    marginBottom: '1rem'
                }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#d33' }}>CSV Parsing Error</h3>
                    <p style={{
                        margin: 0,
                        fontSize: '1rem',
                        lineHeight: '1.5',
                        color: '#800',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace'
                    }}>{csvParseError}</p>
                </div>
                <div style={{
                    backgroundColor: '#fef8e7',
                    border: '1px solid #f9ca24',
                    borderRadius: '4px',
                    padding: '1rem',
                    marginBottom: '1rem'
                }}>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>
                        <strong>Next steps:</strong> Review the error message above, fix the issues in your CSV file, and re-upload.
                    </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: '1rem', padding: "0rem 1rem 1rem 0rem" }} >
                    <CSVButton>Re-Upload Fixed CSV</CSVButton>
                    <button style={{ width: '180px', height: '45px' }} type="button" className="btn" onClick={onClear}>
                        Clear
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container" style={{ position: "relative" }}>
            <p className="title">Select a draft</p>
            <p className="sub-title">Drafts are saved on your local system. Once they are deleted they will be gone.</p>
            {csvError && (
                <div style={{ color: 'red', padding: '0.5rem', marginBottom: '1rem' }}>
                    Error: {csvError}
                </div>
            )}

            {/* CSV Upload Instructions */}
            <div style={{
                backgroundColor: '#f0f8ff',
                border: '1px solid #4a90e2',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                fontSize: '0.95rem',
                lineHeight: '1.6'
            }}>
                <h3 style={{
                    marginTop: 0,
                    marginBottom: '1rem',
                    color: '#2c5282',
                    fontSize: '1.1rem',
                    fontWeight: '600'
                }}>
                    CSV Upload Instructions
                </h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                        <strong style={{ color: '#2c5282' }}>Required Fields:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>
                            All fields from the beginning of the spreadsheet up to the UDT fields are required and must be filled out.
                            This includes fields such as: Account Name, Account HCC ID, TAX ID, addresses, and billing information.
                        </p>
                    </div>
                    <div>
                        <strong style={{ color: '#2c5282' }}>Optional Fields:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>
                            UDT (User Defined Type) fields are located at the end of the spreadsheet and are the only optional fields.
                            These include: Invoice Type, Payment Remit Info, Purchase Order ID, Funding Mode, Account Migration Date,
                            New York State HCRA Election fields, Risk Sharing, and Channel Partner.
                        </p>
                    </div>
                    <div>
                        <strong style={{ color: '#2c5282' }}>Update Behavior:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>
                            Entities that already exist in the system will be updated with the new information.
                            Entities that don't exist will be created as new records.
                        </p>
                    </div>
                </div>
            </div>

            <div className="stickies" style={{ padding: "1rem 0rem", overflowY: 'scroll' }}>
                <div
                    className="sticky"
                    style={{ display: "grid", placeItems: "center", backgroundColor: 'var(--centivo-beige)', cursor: 'pointer' }}
                    onClick={() => {
                        const accountId = crypto.randomUUID();
                        addAccount({ activeStep: 1, accountId, action: ACTIONS.CREATE, subAccounts: [] });
                        navigate(`/config/account-builder/${encodeURIComponent(accountId)}`);
                    }}
                >
                    <div style={{ textAlign: 'center', color: 'var(--darkGrey)' }}>
                        <p style={{ fontWeight: 600, fontSize: "1.2rem" }}>Create Account Draft</p>
                    </div>
                </div>

                {accounts
                    ?.sort((a, b) => b.updatedAt?.localeCompare(a.updatedAt))
                    .map((draft) => (
                        <div className="sticky" key={draft.accountId}>
                            <div className="sticky-header">
                                <h3>{draft.accountName ?? draft.accountId}</h3>
                                <button
                                    className="sticky-delete"
                                    onClick={() => {
                                        removeAccountByAccountId(draft.accountId);
                                    }}
                                >&times;</button>
                            </div>
                            <div
                                className="sticky-body"
                                onClick={() => navigate(`/config/account-builder/${encodeURIComponent(draft.accountId)}`)}
                            >
                                <div className="sticky-section">
                                    <p className="label">Version Date</p>
                                    <p className="value">{getDateString(draft.asOfDate)}</p>
                                </div>
                                <div className="sticky-section">
                                    <p className="label">Account Name</p>
                                    <p className="value">{draft.accountName ?? ''}</p>
                                </div>
                                <div className="sticky-section">
                                    <p className="label">Account HCC ID</p>
                                    <p className="value">{draft.accountHccId ?? ''}</p>
                                </div>
                                <div className="sticky-section">
                                    <p className="label">Active Step</p>
                                    <p className="value">{draft.activeStep} - {TOP_ACCOUNT_BUILDER_INSTRUCTIONS.find((ins) => ins.step === draft.activeStep)?.instructions}</p>
                                </div>
                                <div className="sticky-section">
                                    <p className="label">Action</p>
                                    <p className="value">{draft.action ?? ''}</p>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>
            <DownloadCSVTemplateButton />
            {!isLoading ? (
                <CSVButton>Upload Account CSV</CSVButton>
            ) : (
                <p className="sub-title">Loading CSV upload tool...</p>
            )}
        </div>
    );
};

export default AccountBuilder;
